import { useEffect, useState } from 'react';
import {
  getReglesRappel,
  createRegleRappel,
  updateRegleRappel,
  deleteRegleRappel,
} from '../services/regleRappelService';
import SelectRecherche from '../components/SelectRecherche';
import { getParametresDevis, updateParametresDevis } from '../services/parametreDevisService';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/Parametrage.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';

/**
 * L'heure du passage quotidien qui envoie ce qui part sans bouton : rappels
 * de formation (à chaque délai réglé plus bas), lien d'émargement du matin de chaque journée, relance
 * d'émargement, questionnaire de satisfaction. Réglage transverse, rangé
 * avec les autres (parametres_devis), montré ici où l'on règle les rappels.
 */
function HeureEnvoiAutomatique() {
  const [heure, setHeure] = useState('');
  const [initiale, setInitiale] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getParametresDevis()
      .then((res) => {
        const h = String(res.data?.heureEnvoiAutomatique || '07:30').slice(0, 5);
        setHeure(h);
        setInitiale(h);
      })
      .catch(() => setMessage({ type: 'error', texte: "Heure d'envoi illisible." }));
  }, []);

  async function enregistrer(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await updateParametresDevis({ heureEnvoiAutomatique: heure });
      const h = String(res.data?.heureEnvoiAutomatique || heure).slice(0, 5);
      setHeure(h);
      setInitiale(h);
      setMessage({ type: 'success', texte: `Les envois automatiques partiront chaque jour à ${h}.` });
    } catch (err) {
      setMessage({ type: 'error', texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">Heure des envois automatiques</div>
          <div className="param-card-note">
            Heure quotidienne des envois automatiques. Sauf les liens d’émargement : début de
            séance, puis 13 h.
          </div>
        </div>
      </div>
      <form className="param-form param-card-body" onSubmit={enregistrer}>
        <div className="param-form-row">
          <label className="param-field">
            <span className="param-label">Heure (heure de Paris)</span>
            <input
              type="time"
              className="param-input"
              value={heure}
              onChange={(e) => setHeure(e.target.value)}
              required
            />
          </label>
        </div>
        {message && (
          <div
            className={`param-form-message ${
              message.type === 'error' ? 'param-form-message-error' : 'param-form-message-success'
            }`}
          >
            {message.texte}
          </div>
        )}
        <div className="param-form-actions">
          <button
            type="submit"
            className="param-btn param-btn-primary"
            disabled={saving || !heure || heure === initiale}
          >
            {saving ? 'Enregistrement' : 'Enregistrer l’heure'}
          </button>
        </div>
      </form>
    </div>
  );
}


/**
 * Envoi automatique de la convocation (migration 055).
 *
 * C'était le dernier envoi entièrement manuel de la chaîne. Coché, le
 * passage quotidien génère au besoin les documents de la séance — convocation,
 * feuille de présence, convocation du formateur — puis expédie, exactement
 * comme le bouton de la page Convocations.
 */
function EnvoiConvocationAuto() {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getParametresDevis()
      .then((res) => {
        const valeurs = {
          actif: Boolean(res.data?.convocationEnvoiAuto),
          delai: Number(res.data?.convocationDelaiJours ?? 7),
        };
        setForm(valeurs);
        setInitial(valeurs);
      })
      .catch(() => setMessage({ type: 'error', texte: 'Réglage de la convocation illisible.' }));
  }, []);

  const modifie =
    form && initial && (form.actif !== initial.actif || Number(form.delai) !== initial.delai);

  async function enregistrer(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await updateParametresDevis({
        convocationEnvoiAuto: form.actif,
        convocationDelaiJours: Number(form.delai),
      });
      const valeurs = {
        actif: Boolean(res.data?.convocationEnvoiAuto),
        delai: Number(res.data?.convocationDelaiJours ?? form.delai),
      };
      setForm(valeurs);
      setInitial(valeurs);
      setMessage({
        type: 'success',
        texte: valeurs.actif
          ? `Les convocations partiront seules, ${
              valeurs.delai === 0 ? 'le matin de la séance' : `${valeurs.delai} jour(s) avant`
            }.`
          : 'Envoi automatique désactivé : les convocations restent au bouton.',
      });
    } catch (err) {
      setMessage({ type: 'error', texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">Envoi automatique des convocations</div>
          <div className="param-card-note">
            Coché, la convocation part seule (copie au client) ; sinon, au bouton de la page
            Convocations.
          </div>
        </div>
      </div>
      {form && (
        <form className="param-form param-card-body" onSubmit={enregistrer}>
          <label className="param-checkbox-field">
            <input
              type="checkbox"
              checked={form.actif}
              onChange={(e) => setForm((p) => ({ ...p, actif: e.target.checked }))}
              disabled={saving}
            />
            <span>Envoyer les convocations automatiquement</span>
          </label>

          <div className="param-form-row">
            <label className="param-field">
              <span className="param-label">Combien de jours avant la séance</span>
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                className="param-input"
                value={form.delai}
                onChange={(e) => setForm((p) => ({ ...p, delai: e.target.value }))}
                disabled={saving || !form.actif}
                required
              />
            </label>
          </div>

          <p className="param-hint">
            {Number(form.delai) === 0
              ? 'Jour J : le matin de la première journée.'
              : `J−${Number(form.delai)} : ${Number(form.delai)} jour(s) avant la première journée.`}
            {' '}Planifiée plus tard, elle part dès l’enregistrement.
          </p>

          {message && (
            <div
              className={`param-form-message ${
                message.type === 'error' ? 'param-form-message-error' : 'param-form-message-success'
              }`}
            >
              {message.texte}
            </div>
          )}

          <div className="param-form-actions">
            <button
              type="submit"
              className="param-btn param-btn-primary"
              disabled={saving || !modifie}
            >
              {saving ? 'Enregistrement' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Les deux questionnaires de satisfaction (migration 055).
 *
 * Stagiaires et formateur ne répondent pas au même formulaire. Leur URL était
 * écrite dans le corps du modèle : la changer demandait de la retrouver au
 * milieu du texte. Elle devient un champ, et le modèle ne porte plus que la
 * balise {{lien_questionnaire}} — le texte se retouche à côté, sans risque
 * pour le lien.
 */
function QuestionnairesSatisfaction({ onOuvrirModele }) {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getParametresDevis()
      .then((res) => {
        const valeurs = {
          stagiaire: res.data?.lienSatisfactionStagiaire || '',
          formateur: res.data?.lienSatisfactionFormateur || '',
        };
        setForm(valeurs);
        setInitial(valeurs);
      })
      .catch(() => setMessage({ type: 'error', texte: 'Liens des questionnaires illisibles.' }));
  }, []);

  const modifie =
    form &&
    initial &&
    (form.stagiaire.trim() !== initial.stagiaire || form.formateur.trim() !== initial.formateur);

  async function enregistrer(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await updateParametresDevis({
        lienSatisfactionStagiaire: form.stagiaire.trim(),
        lienSatisfactionFormateur: form.formateur.trim(),
      });
      const valeurs = {
        stagiaire: res.data?.lienSatisfactionStagiaire || '',
        formateur: res.data?.lienSatisfactionFormateur || '',
      };
      setForm(valeurs);
      setInitial(valeurs);
      setMessage({ type: 'success', texte: 'Liens enregistrés.' });
    } catch (err) {
      setMessage({ type: 'error', texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">Questionnaires de satisfaction</div>
          <div className="param-card-note">
            Chaque lien remplace la balise <span className="param-code">{'{{lien_questionnaire}}'}</span> de
            son email. Vide : aucun lien.
          </div>
        </div>
      </div>
      {form && (
        <form className="param-form param-card-body" onSubmit={enregistrer}>
          <RemplirParTexte
            variante="param"
            contexte="liens des questionnaires de satisfaction (stagiaires et formateur)"
            champs={[
              { cle: 'stagiaire', libelle: 'Lien du questionnaire — stagiaires' },
              { cle: 'formateur', libelle: 'Lien du questionnaire — formateur' },
            ]}
            onValeurs={(v) => setForm((p) => ({ ...p, ...v }))}
          />

          <div className="param-field">
            <span className="param-label">Lien du questionnaire — stagiaires</span>
            <input
              type="url"
              className="param-input"
              placeholder="https://tally.so/r/..."
              value={form.stagiaire}
              onChange={(e) => setForm((p) => ({ ...p, stagiaire: e.target.value }))}
              disabled={saving}
            />
            <button
              type="button"
              className="param-btn param-btn-sm"
              onClick={() => onOuvrirModele('formation_satisfaction')}
            >
              Modifier le texte de l’email
            </button>
          </div>

          <div className="param-field">
            <span className="param-label">Lien du questionnaire — formateur</span>
            <input
              type="url"
              className="param-input"
              placeholder="https://tally.so/r/..."
              value={form.formateur}
              onChange={(e) => setForm((p) => ({ ...p, formateur: e.target.value }))}
              disabled={saving}
            />
            <button
              type="button"
              className="param-btn param-btn-sm"
              onClick={() => onOuvrirModele('formation_satisfaction_formateur')}
            >
              Modifier le texte de l’email
            </button>
          </div>

          {message && (
            <div
              className={`param-form-message ${
                message.type === 'error' ? 'param-form-message-error' : 'param-form-message-success'
              }`}
            >
              {message.texte}
            </div>
          )}

          <div className="param-form-actions">
            <button
              type="submit"
              className="param-btn param-btn-primary"
              disabled={saving || !modifie}
            >
              {saving ? 'Enregistrement' : 'Enregistrer les liens'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Les délais qui étaient écrits en dur (migration 065).
 *
 * Chacun dit QUAND une chose part ou se signale. Ils étaient dans le code ou
 * dans une variable d'environnement, hors de portée de qui gère les
 * formations. Deux réglages techniques restent volontairement ailleurs (le
 * rattrapage d'un serveur arrêté, le parallélisme de l'IA) : ce ne sont pas
 * des règles métier.
 */
const DELAIS_FORMATION = [
  {
    champ: 'notificationFormationJours',
    groupe: 'alertes',
    label: 'Formation proche dans la cloche',
    unite: 'j avant',
    min: 0,
    aide: (n) => (n === 0 ? 'Le jour même.' : `Dès ${n} jour(s) avant la formation.`),
  },
  {
    champ: 'alertePlanificationJours',
    groupe: 'alertes',
    label: 'Planification ou contrat manquant',
    unite: 'j avant',
    min: 0,
    aide: (n) => `Cloche et email, ${n} jour(s) avant la formation.`,
  },
  {
    champ: 'alerteAttestationJours',
    groupe: 'alertes',
    label: 'Attestation formateur à renouveler',
    unite: 'j avant',
    min: 0,
    aide: (n) => `Email ${n} jour(s) avant l’échéance URSSAF ou RC Pro.`,
  },
  {
    champ: 'satisfactionDelaiJours',
    groupe: 'apres',
    label: 'Questionnaire de satisfaction',
    unite: 'j après',
    min: 0,
    aide: (n) =>
      n === 0
        ? 'Attention : partirait avant la fin de la formation.'
        : `${n} jour(s) après la dernière journée.`,
  },
  {
    champ: 'relanceEmargementDebutJours',
    groupe: 'apres',
    label: 'Relance d’émargement',
    unite: 'j après',
    min: 0,
    aide: (n) => (n === 0 ? 'Le jour même.' : `${n} jour(s) après la journée.`),
  },
  {
    champ: 'relanceEmargementDureeJours',
    groupe: 'apres',
    label: 'Durée de la relance',
    unite: 'jours',
    min: 1,
    aide: (n) => `Chaque jour pendant ${n} jour(s), tant que la case est vide.`,
  },
];

const GROUPES_DELAIS = [
  { cle: 'alertes', titre: 'Alertes à l’équipe' },
  { cle: 'apres', titre: 'Après la formation' },
];

function DelaisFormation() {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getParametresDevis()
      .then((res) => {
        const d = res.data || {};
        const valeurs = Object.fromEntries(
          DELAIS_FORMATION.map((x) => [x.champ, String(d[x.champ] ?? '')])
        );
        setForm(valeurs);
        setInitial(valeurs);
      })
      .catch(() => setMessage({ type: 'error', texte: 'Délais illisibles.' }));
  }, []);

  const modifie =
    form && initial && DELAIS_FORMATION.some((x) => form[x.champ] !== initial[x.champ]);

  async function enregistrer(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const corps = Object.fromEntries(
        DELAIS_FORMATION.map((x) => [x.champ, Number(form[x.champ])])
      );
      const res = await updateParametresDevis(corps);
      const d = res.data || {};
      const valeurs = Object.fromEntries(
        DELAIS_FORMATION.map((x) => [x.champ, String(d[x.champ] ?? form[x.champ])])
      );
      setForm(valeurs);
      setInitial(valeurs);
      setMessage({
        type: 'success',
        texte: 'Délais enregistrés.',
      });
    } catch (err) {
      setMessage({ type: 'error', texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">Délais des envois et des alertes</div>
          <div className="param-card-note">Les rappels de formation se règlent plus bas.</div>
        </div>
      </div>
      {form && (
        <form className="param-card-body delais" onSubmit={enregistrer}>
          {/* Les champs sont des chaînes (input number) : comme au chargement. */}
          <RemplirParTexte
            variante="param"
            contexte="délais, en jours, des alertes et des envois automatiques autour d'une formation"
            champs={DELAIS_FORMATION.map((x) => ({ cle: x.champ, libelle: x.label, type: 'nombre' }))}
            onValeurs={(v) =>
              setForm((p) => ({
                ...p,
                ...Object.fromEntries(Object.entries(v).map(([k, n]) => [k, String(n)])),
              }))
            }
          />

          {GROUPES_DELAIS.map((g) => (
            <section className="delais-groupe" key={g.cle}>
              <h3 className="delais-groupe-titre">{g.titre}</h3>
              <div className="delais-grille">
                {DELAIS_FORMATION.filter((x) => x.groupe === g.cle).map((x) => {
                  const n = Number(form[x.champ]);
                  const change = form[x.champ] !== initial[x.champ];
                  return (
                    <label
                      className={`delai-tuile${change ? ' delai-tuile-modifiee' : ''}`}
                      key={x.champ}
                    >
                      <span className="delai-texte">
                        <span className="delai-libelle">{x.label}</span>
                        <span className="delai-aide">
                          {Number.isFinite(n) && form[x.champ] !== '' ? x.aide(n) : '—'}
                        </span>
                      </span>
                      <span className="delai-valeur">
                        <input
                          type="number"
                          min={x.min}
                          max="365"
                          step="1"
                          value={form[x.champ]}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((p) => ({ ...p, [x.champ]: v }));
                          }}
                          disabled={saving}
                          required
                          aria-label={x.label}
                        />
                        <span className="delai-unite">{x.unite}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="delais-pied">
            {message ? (
              <div
                className={`param-form-message ${
                  message.type === 'error' ? 'param-form-message-error' : 'param-form-message-success'
                }`}
              >
                {message.texte}
              </div>
            ) : (
              <span className="delais-pied-note">
                {modifie ? 'Modifications non enregistrées.' : 'Appliqués dès le prochain passage.'}
              </span>
            )}
            <button
              type="submit"
              className="param-btn param-btn-primary"
              disabled={saving || !modifie}
            >
              {saving ? 'Enregistrement' : 'Enregistrer les délais'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Les emails qui partent sans bouton.
 *
 * Leur texte vivait en base sans aucun écran pour l'atteindre : les modifier
 * demandait une migration. Chacun s'ouvre ici dans le même panneau que les
 * emails du devis ou du contrat. La satisfaction a sa propre carte, ses liens
 * se réglant à côté.
 */
const EMAILS_AUTOMATIQUES = [
  {
    type: 'attestation_formateur',
    titre: 'Alerte interne — attestations formateurs',
    quand:
      'Chaque matin, avant l’échéance URSSAF ou RC Pro d’un formateur. Balises {{nombre}} et {{details}}.',
  },
  {
    type: 'formateur_chevauchement',
    titre: 'Alerte interne — chevauchement de planning',
    quand:
      'Formateur assigné à deux séances qui se chevauchent. Balises {{formateur}}, {{details}}, {{lien}}.',
  },
  {
    type: 'formation_rappel',
    titre: 'Rappel de formation',
    quand:
      'Aux délais des « Rappels de formation ». {{quand}} donne « demain », « dans une semaine »…',
  },
  {
    type: 'formation_jour_j',
    titre: 'Jour J — signature en ligne',
    quand:
      'Au début de la séance puis à 13 h, avec le lien d’émargement de chacun. {{quand_formation}} donne « aujourd’hui », « demain » ou la date — et toutes les dates quand le lien ouvre toutes les journées.',
  },
  {
    type: 'emargement_relance',
    titre: 'Relance d’émargement',
    quand: 'Le lendemain d’une journée, aux seules personnes dont la case est restée vide.',
  },
  {
    type: 'formation_rappel_formateur',
    titre: 'Rappel de formation — formateur',
    quand: 'Aux mêmes délais, pour le formateur.',
  },
  {
    type: 'formation_jour_j_formateur',
    titre: 'Jour J — formateur',
    quand: 'Aux mêmes moments, avec son lien de signature.',
  },
  {
    type: 'emargement_relance_formateur',
    titre: 'Relance d’émargement — formateur',
    quand: 'Le lendemain, si sa propre case est restée vide.',
  },
  {
    type: 'contrat_relance',
    titre: 'Contrat — relance',
    quand:
      'Aux délais réglés ci-dessous, si le contrat n’est pas signé.',
  },
  {
    type: 'feuille_presence_signee',
    titre: 'Feuille de présence signée',
    quand:
      'Au bouton, depuis la page Feuille de présence : le document émargé part au client.',
  },
  {
    type: 'planification_manquante',
    titre: 'Alerte interne — dossiers à surveiller',
    quand:
      'Chaque jour : formations non planifiées, contrats non signés, élus sans email.',
  },
];

function EmailsAutomatiques({ onOuvrirModele }) {
  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">
            Textes des emails de formation
            <span className="param-compteur">{EMAILS_AUTOMATIQUES.length}</span>
          </div>
          <div className="param-card-note">
            Objet et corps des emails de formation. La relance de devis se règle page Devis.
          </div>
        </div>
      </div>
      <div className="param-table-wrap">
        <table className="param-table">
          <thead>
            <tr>
              <th>EMAIL</th>
              <th className="param-th-actions">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {EMAILS_AUTOMATIQUES.map((email) => (
              <tr key={email.type}>
                <td>
                  <div className="param-intitule">{email.titre}</div>
                  <div className="param-declenchement">{email.quand}</div>
                </td>
                <td>
                  <div className="param-row-actions">
                    <button
                      type="button"
                      className="param-btn param-btn-sm"
                      onClick={() => onOuvrirModele(email.type)}
                    >
                      Modifier le texte
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Les deux natures de délai ne se comptent pas depuis le même repère : l'une
 * court après l'envoi du devis, l'autre avant la date de formation. Tout
 * l'affichage (signe J+/J−, phrase d'aide) découle de cette table.
 */
const TYPES = [
  {
    value: 'relance_devis',
    titre: 'Relance des demandes de devis',
    soustitre:
      'Jours sans réponse avant que la demande repasse « À relancer ».',
    signe: '+',
    repere: "après l'envoi du devis",
    ajout: '+ Ajouter une relance',
    exemple: 'Première relance',
    ton: 'relance',
  },
  {
    value: 'rappel_formation',
    titre: 'Rappels de formation',
    soustitre:
      'Chaque règle active envoie un rappel aux élus et au formateur avant la première journée.',
    signe: '−',
    repere: 'avant la formation',
    ajout: '+ Ajouter un rappel',
    exemple: 'Rappel une semaine avant',
    ton: 'formation',
  },
];

const REGLE_VIDE = {
  type: 'relance_devis',
  libelle: '',
  delaiJours: '',
  commentaire: '',
  actif: true,
};

function regleToForm(regle) {
  return {
    type: regle.type,
    libelle: regle.libelle ?? '',
    delaiJours: regle.delaiJours ?? '',
    commentaire: regle.commentaire ?? '',
    actif: regle.actif ?? true,
  };
}

const typeInfo = (valeur) => TYPES.find((t) => t.value === valeur) || TYPES[0];

/** « J+7 », « J−1 », et « Jour J » quand le délai est nul. */
function marqueur(type, delaiJours) {
  const jours = Number(delaiJours);
  if (!Number.isFinite(jours)) return '—';
  if (jours === 0) return 'Jour J';
  return `J${typeInfo(type).signe}${jours}`;
}

/** « 7 jours après l'envoi du devis » — la phrase qui lève l'ambiguïté. */
function phraseDelai(type, delaiJours) {
  const jours = Number(delaiJours);
  const info = typeInfo(type);
  if (!Number.isFinite(jours)) return '';
  if (jours === 0) return `Le jour même, ${info.repere.replace(/^(après|avant) /, '')}`;
  return `${jours} jour${jours > 1 ? 's' : ''} ${info.repere}`;
}

function ParametrageRappels() {
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(REGLE_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [formError, setFormError] = useState(null);

  const [suppression, setSuppression] = useState(null);
  const [flash, setFlash] = useState(null);

  // Type du modèle d'email ouvert dans le panneau, ou null. Les cartes des
  // envois automatiques le posent ; la page l'affiche.
  const [modeleEmail, setModeleEmail] = useState(null);

  function chargerRegles() {
    setLoading(true);
    setError(null);
    return getReglesRappel()
      .then((res) => setRegles(res.data || []))
      .catch(() => setError('Impossible de charger les règles de rappel.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    chargerRegles();
  }, []);

  function ouvrirCreation(type) {
    setEdition({ id: null });
    setForm({ ...REGLE_VIDE, type });
    setFormError(null);
  }

  function ouvrirEdition(regle) {
    setEdition({ id: regle.id });
    setForm(regleToForm(regle));
    setFormError(null);
  }

  function fermerModale() {
    setEdition(null);
    setFormError(null);
  }

  function handleFieldChange(champ) {
    return (e) => {
      const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [champ]: valeur }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload = { ...form, delaiJours: Number(form.delaiJours) };

    try {
      if (edition.id === null) {
        await createRegleRappel(payload);
        setFlash('Règle créée.');
      } else {
        await updateRegleRappel(edition.id, payload);
        setFlash('Règle mise à jour.');
      }
      await chargerRegles();
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteRegleRappel(suppression.id);
      await chargerRegles();
      setFlash('Règle supprimée.');
      setSuppression(null);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSaving(false);
    }
  }

  const infoForm = typeInfo(form.type);

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Rappels</h1>
          <p className="param-subtitle">
            Tout ce qui part sans bouton : heure, convocations, délais, emails et questionnaires.
          </p>
        </div>
      </div>

      {flash && (
        <div className="param-toolbar">
          <span className="param-flash">{flash}</span>
        </div>
      )}

      <div className="param-scroll param-scroll-cartes">
        <HeureEnvoiAutomatique />
        <EnvoiConvocationAuto />
        <DelaisFormation />
        <QuestionnairesSatisfaction onOuvrirModele={setModeleEmail} />
        <EmailsAutomatiques onOuvrirModele={setModeleEmail} />

        {loading && (
          <div className="param-card">
            <div className="param-state">Chargement des règles...</div>
          </div>
        )}

        {!loading && error && (
          <div className="param-card">
            <div className="param-state param-state-error">{error}</div>
          </div>
        )}

        {!loading &&
          !error &&
          TYPES.map((info) => {
            const duType = regles.filter((r) => r.type === info.value);

            return (
              <div className="param-card" key={info.value}>
                <div className="param-card-header">
                  <div>
                    <div className="param-section-titre">
                      {info.titre}
                      <span className="param-compteur">{duType.length}</span>
                    </div>
                    <div className="param-card-note">{info.soustitre}</div>
                  </div>
                  <button
                    type="button"
                    className="param-btn param-btn-sm"
                    onClick={() => ouvrirCreation(info.value)}
                  >
                    {info.ajout}
                  </button>
                </div>

                {duType.length === 0 ? (
                  <div className="param-empty">
                    <div className="param-empty-title">Aucune règle pour l&apos;instant.</div>
                    <div className="param-empty-subtitle">
                      Sans règle, aucun message n&apos;est programmé pour cette étape.
                    </div>
                  </div>
                ) : (
                  <div className="param-table-wrap">
                    <table className="param-table">
                      <thead>
                        <tr>
                          <th className="param-th-delai">DÉLAI</th>
                          <th>LIBELLÉ</th>
                          <th className="param-th-etat">ÉTAT</th>
                          <th className="param-th-actions">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duType.map((regle) => (
                          <tr key={regle.id}>
                            <td className="param-td-delai">
                              <span className={`param-delai param-delai-${info.ton}`}>
                                {marqueur(regle.type, regle.delaiJours)}
                              </span>
                            </td>
                            <td>
                              <div className="param-intitule">{regle.libelle}</div>
                              {/* La pastille seule ne dit pas depuis quel repère
                                  le délai se compte : la phrase le rappelle. */}
                              <div className="param-declenchement">
                                {phraseDelai(regle.type, regle.delaiJours)}
                              </div>
                              {regle.commentaire && (
                                <div className="param-description">{regle.commentaire}</div>
                              )}
                            </td>
                            <td>
                              <span className={`param-pill${regle.actif ? '' : ' param-pill-muted'}`}>
                                {regle.actif ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className="param-row-actions">
                                <button
                                  type="button"
                                  className="param-btn param-btn-sm"
                                  onClick={() => ouvrirEdition(regle)}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="param-btn param-btn-sm param-btn-danger"
                                  onClick={() => setSuppression(regle)}
                                >
                                  Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {modeleEmail && (
        <ModeleEmailPanneau type={modeleEmail} onClose={() => setModeleEmail(null)} />
      )}

      {edition && (
        <div className="param-modal-overlay" onClick={fermerModale}>
          <div className="param-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">
                {edition.id === null ? 'Nouvelle règle de rappel' : 'Modifier la règle'}
              </h2>
              <button type="button" className="param-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && (
                <div className="param-form-message param-form-message-error">{formError}</div>
              )}

              {/* Création comme modification ; la case « Règle active » reste à cocher à la main. */}
              <RemplirParTexte
                variante="param"
                contexte="règle de rappel (relance de devis ou rappel de formation, avec son délai en jours)"
                champs={[
                  {
                    cle: 'type',
                    libelle: 'Type de message',
                    type: 'choix',
                    options: TYPES.map((t) => ({ valeur: t.value, libelle: t.titre })),
                  },
                  { cle: 'delaiJours', libelle: 'Délai (jours)', type: 'nombre' },
                  { cle: 'libelle', libelle: 'Libellé' },
                  { cle: 'commentaire', libelle: 'Commentaire' },
                ]}
                onValeurs={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    ...v,
                    ...(v.delaiJours !== undefined ? { delaiJours: String(v.delaiJours) } : {}),
                  }))
                }
              />

              <div className="param-form-row">
                <label className="param-field param-field-grow">
                  <span className="param-label">Type de message</span>
                  <SelectRecherche
                    className="param-select"
                    value={form.type}
                    onChange={handleFieldChange('type')}
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.titre}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>

                <label className="param-field">
                  <span className="param-label">Délai (jours)</span>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    step="1"
                    className="param-input"
                    value={form.delaiJours}
                    onChange={handleFieldChange('delaiJours')}
                    required
                  />
                </label>
              </div>

              {/* Le nombre saisi ne dit pas depuis quand il court : on le
                  reformule sous le champ. */}
              <p className="param-hint">
                {form.delaiJours === ''
                  ? `Le délai se compte ${infoForm.repere}.`
                  : `Envoi ${phraseDelai(form.type, form.delaiJours).toLowerCase()}.`}
              </p>

              <label className="param-field">
                <span className="param-label">Libellé</span>
                <input
                  type="text"
                  className="param-input"
                  maxLength={120}
                  placeholder={infoForm.exemple}
                  value={form.libelle}
                  onChange={handleFieldChange('libelle')}
                  required
                />
              </label>

              <label className="param-field">
                <span className="param-label">Commentaire</span>
                <textarea
                  className="param-textarea"
                  rows={3}
                  placeholder="À quoi sert ce message, ce qu'il doit contenir..."
                  value={form.commentaire}
                  onChange={handleFieldChange('commentaire')}
                />
              </label>

              <label className="param-checkbox-field">
                <input type="checkbox" checked={form.actif} onChange={handleFieldChange('actif')} />
                <span>Règle active (le message est programmé)</span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de la règle" estimation />
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Créer' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer la règle</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Voulez-vous vraiment supprimer <strong>{suppression.libelle}</strong> (
                {phraseDelai(suppression.type, suppression.delaiJours).toLowerCase()}) ? Ce message
                ne sera plus programmé.
              </p>
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParametrageRappels;
